// The cutaway panel: plane controls that cut the 3D engine open.
//
// It lists the view state's cutaway planes (at most MAX_CUT_PLANES), each with
// the side it cuts away and its offset, and adds, removes and clears them. It
// only writes the view state; the scene draws whatever planes are there
// (ADR 0011), and the camera bar's cutaway views set them too, so the rows
// follow the state rather than keeping their own.

import "./cutaway.css";
import { defineFeature } from "../../../registry";
import { el } from "../../controls/dom";
import { MAX_CUT_PLANES, type CutPlane } from "../../../state/view-state";
import {
  OFFSET_MAX,
  OFFSET_MIN,
  OFFSET_STEP,
  SIDES,
  addPlane,
  removePlane,
  setOffset,
  setSide,
  sideOf,
  type Side,
} from "./planes";
import { strings } from "./strings";

const MM = 1000;

interface Row {
  root: HTMLElement;
  side: HTMLSelectElement;
  custom: HTMLOptionElement;
  range: HTMLInputElement;
  number: HTMLInputElement;
}

export default defineFeature({
  id: "cutaway",
  title: strings.title,
  slot: "panel",
  order: 20,
  mount({ root, state }) {
    const intro = el("p", { class: "panel-note" }, strings.intro);
    const list = el("ol", { class: "cut-planes" });
    const empty = el("p", { class: "panel-note cut-empty" }, strings.none);
    const add = el("button", { type: "button", "data-action": "add" }, strings.add);
    const clear = el("button", { type: "button", "data-action": "clear" }, strings.clear);
    const actions = el("div", { class: "cut-actions" }, add, clear);
    root.append(intro, list, empty, actions);

    const rows: Row[] = [];
    const planes = (): CutPlane[] => state.state.cutaway;
    const write = (next: CutPlane[]): void => void state.set({ cutaway: next });

    const makeRow = (index: number): Row => {
      const n = index + 1;
      const side = el("select", { "data-field": "side", "aria-label": `${strings.plane(n)}: ${strings.side}` });
      for (const s of SIDES) side.append(el("option", { value: s.side }, strings.sides[s.side]));
      const custom = el("option", { value: "custom", disabled: true }, strings.sides.custom);
      side.append(custom);
      const range = el("input", {
        type: "range",
        "data-field": "offset",
        min: OFFSET_MIN * MM,
        max: OFFSET_MAX * MM,
        step: OFFSET_STEP * MM,
        "aria-label": `${strings.plane(n)}: ${strings.offset}`,
        title: strings.offsetHint,
      });
      const number = el("input", {
        type: "number",
        "data-field": "offset-mm",
        step: 0.1,
        "aria-label": `${strings.plane(n)}: ${strings.offset} (${strings.offsetUnit})`,
      });
      const remove = el("button", { type: "button", "data-action": "remove", "aria-label": strings.removeLabel(n) }, strings.remove);
      const rowRoot = el(
        "li",
        { class: "cut-plane", "data-plane": index },
        el("label", { class: "cut-field" }, el("span", {}, strings.side), side),
        el("label", { class: "cut-field cut-offset" }, el("span", {}, strings.offset), range, number, el("span", { class: "cut-unit" }, strings.offsetUnit)),
        remove,
      );
      side.addEventListener("change", () => write(setSide(planes(), index, side.value as Side)));
      range.addEventListener("input", () => write(setOffset(planes(), index, Number(range.value) / MM)));
      number.addEventListener("change", () => {
        const mm = Number(number.value);
        if (Number.isFinite(mm)) write(setOffset(planes(), index, mm / MM));
      });
      remove.addEventListener("click", () => write(removePlane(planes(), index)));
      return { root: rowRoot, side, custom, range, number };
    };

    const render = (): void => {
      const current = planes();
      while (rows.length > current.length) rows.pop()!.root.remove();
      while (rows.length < current.length) {
        const row = makeRow(rows.length);
        rows.push(row);
        list.append(row.root);
      }
      current.forEach((plane, i) => {
        const row = rows[i]!;
        const side = sideOf(plane);
        row.custom.hidden = side !== null;
        row.side.value = side ?? "custom";
        const mm = plane.offset * MM;
        // Leave a control alone while it is being used, so typing and dragging are not undone.
        if (document.activeElement !== row.range) row.range.value = String(mm);
        if (document.activeElement !== row.number) row.number.value = String(Math.round(mm * 10) / 10);
      });
      empty.hidden = current.length > 0;
      list.hidden = current.length === 0;
      clear.disabled = current.length === 0;
      add.disabled = current.length >= MAX_CUT_PLANES;
      add.title = add.disabled ? strings.full(MAX_CUT_PLANES) : "";
      root.dataset["planes"] = String(current.length);
    };

    add.addEventListener("click", () => write(addPlane(planes())));
    clear.addEventListener("click", () => write([]));
    const off = state.subscribe((s, previous) => {
      if (s.cutaway !== previous.cutaway) render();
    });
    render();
    return off;
  },
});
